import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { SIDEBAR_GRADIENT } from "./constants";
import type { ShellBackLink, ShellNavItem, ShellNavSection } from "./types";

interface ShellSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  sections: ShellNavSection[];
  header: ReactNode;
  backLink?: ShellBackLink;
  footer?: ReactNode;
  expandedWidth?: number;
  collapsedWidth?: number;
  isItemActive?: (pathname: string, item: ShellNavItem) => boolean;
  renderItemBadge?: (item: ShellNavItem) => ReactNode;
}

function defaultIsItemActive(pathname: string, item: ShellNavItem): boolean {
  return (
    pathname === item.href ||
    (item.href !== "/dashboard" &&
      item.href !== "/settings" &&
      pathname.startsWith(item.href))
  );
}

export function ShellSidebar({
  collapsed,
  onToggle,
  sections,
  header,
  backLink,
  footer,
  expandedWidth = 260,
  collapsedWidth = 72,
  isItemActive = defaultIsItemActive,
  renderItemBadge,
}: ShellSidebarProps) {
  const location = useLocation();

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
      transition={{ duration: 0.28, ease: "easeInOut" }}
      className="relative flex shrink-0 flex-col shadow-2xl z-50 overflow-hidden border-r border-white/10"
      style={{ background: SIDEBAR_GRADIENT }}
    >
      <div className="flex min-h-[4.5rem] items-center border-b border-white/10 px-3 py-3 shrink-0">
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">{header}</AnimatePresence>
        </div>
      </div>

      {backLink && (
        <div className="px-3 pt-3 pb-1 shrink-0">
          <button
            type="button"
            onClick={backLink.onClick}
            className={cn(
              "flex items-center gap-2.5 w-full rounded-xl px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/10 transition-all duration-150",
              collapsed && "justify-center px-0",
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  className="text-[12.5px] font-semibold whitespace-nowrap"
                >
                  {backLink.label}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2 scrollbar-none">
        {sections.map((section, si) => (
          <div key={section.label} className={si > 0 ? "mt-1 pt-1 border-t border-white/8" : ""}>
            {!collapsed && (
              <p className="px-5 pt-3 pb-1 text-[8px] font-black uppercase tracking-[0.3em] text-white/25">
                {section.label}
              </p>
            )}
            {section.items.map((item) => {
              const active = isItemActive(location.pathname, item);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "group relative flex items-center mx-2 px-3 py-2.5 rounded-xl transition-all duration-150",
                    collapsed ? "justify-center" : "gap-3",
                    active
                      ? "bg-white/20 text-white shadow-lg shadow-black/10"
                      : "text-white/55 hover:text-white hover:bg-white/10",
                  )}
                >
                  {active && !collapsed && (
                    <span className="absolute left-1.5 h-1.5 w-1.5 rounded-full bg-sky-300" />
                  )}
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  <AnimatePresence>
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.15 }}
                        className="text-[13px] font-semibold whitespace-nowrap"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {renderItemBadge?.(item)}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {footer}

      <div className="p-3 border-t border-white/10 bg-black/10 shrink-0">
        <button
          type="button"
          onClick={onToggle}
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 border border-white/10 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </motion.aside>
  );
}
